import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get } from '../../api/http';
import { Event } from '../../types/event';

interface PrivateEventResponse {
  success: boolean;
  message: string;
  data: {
    event: Event;
    invite: {
      email: string;
      token: string;
    };
  };
}

const PrivateEventLoader: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      if (!token) {
        setError("Invalid token");
        setLoading(false);
        return;
      }

      try {
        // Construct URL for private event check
        // Assuming API_BASE_URL is set correctly in http module
        const response = await get<PrivateEventResponse>(`/event/events/private/${token}/`);
        
        if (response.data.success) {
          const { event, invite } = response.data.data;
          if (!event?.event_uuid) {
            setError("This event is no longer available.");
            return;
          }
          navigate(`/embedded_checkout_page/${event.event_uuid}`, {
            replace: true,
            state: { inviteToken: invite.token }
          });
        } else {
          setError(response.data.message || "Access denied");
        }
      } catch (err: any) {
        console.error("Private event check failed", err);
        setError(err.response?.data?.message || "Failed to verify access. The link may be invalid or expired.");
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [token, navigate]);

  if (loading) {
     return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          background: '#0a0a0a', 
          color: 'white',
          flexDirection: 'column'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderRadius: '50%',
            borderTopColor: '#fff',
            animation: 'spin 1s ease-in-out infinite'
          }}></div>
          <p style={{ marginTop: '20px' }}>Verifying access...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
     );
  }

  if (error) {
    return (
      <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          background: '#0a0a0a', 
          color: 'white',
          flexDirection: 'column',
          padding: '20px',
          textAlign: 'center'
        }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <h2 style={{ fontSize: '24px', marginBottom: '12px' }}>Invite Link Expired</h2>
        <p style={{ color: '#aaa', marginBottom: '32px', maxWidth: '400px', lineHeight: '1.6' }}>
          This private event invite link is no longer valid. The token may have expired or already been used. Please request a new invite from the event organizer.
        </p>
        <button 
          onClick={() => navigate('/events')}
          style={{
            padding: '12px 24px',
            background: '#8b5cf6',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '16px'
          }}
        >
          Browse Events
        </button>
      </div>
    );
  }

  return null;
};

export default PrivateEventLoader;
